import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Crosshair, Search, X, Loader2, Star, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function OriginPicker({ center, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState([]);
  const [pendingSave, setPendingSave] = useState(null); // {lat,lng,address}
  const [saveName, setSaveName] = useState("");
  const boxRef = useRef(null);

  const loadSaved = useCallback(() => {
    api.get("/me/origins").then((r) => setSaved(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (q.trim().length < 3) { setResults([]); return; }
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const near = center ? `&near_lat=${center[0]}&near_lng=${center[1]}` : "";
        const r = await api.get(`/geocode?q=${encodeURIComponent(q.trim())}${near}`);
        setResults(r.data.results || []);
      } catch { setResults([]); } finally { setBusy(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [q, center]);

  const shortLabel = (full) => full.split(",").slice(0, 2).join(",");

  const pick = (res) => {
    onChange({ lat: res.lat, lng: res.lng, label: res.label ? shortLabel(res.label) : res.label });
    setPendingSave({ lat: res.lat, lng: res.lng, address: res.label || "" });
    setOpen(false);
    setQ("");
    setResults([]);
  };

  const pickSaved = (o) => {
    onChange({ lat: o.lat, lng: o.lng, label: o.label });
    setPendingSave(null);
    setOpen(false);
  };

  const confirmSave = async () => {
    if (!saveName.trim() || !pendingSave) return;
    try {
      const r = await api.post("/me/origins", {
        label: saveName.trim(), lat: pendingSave.lat, lng: pendingSave.lng, address: pendingSave.address,
      });
      setSaved((s) => [...s, r.data]);
      onChange({ lat: r.data.lat, lng: r.data.lng, label: r.data.label });
      toast.success(`Saved "${r.data.label}"`);
      setPendingSave(null); setSaveName("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save.");
    }
  };

  const removeSaved = async (e, o) => {
    e.stopPropagation();
    try {
      await api.delete(`/me/origins/${o.id}`);
      setSaved((s) => s.filter((x) => x.id !== o.id));
      toast.success(`Removed "${o.label}"`);
    } catch { toast.error("Could not remove."); }
  };

  const label = value ? (value.label || value.name) : "My location";

  return (
    <div className="relative" ref={boxRef} data-testid="origin-picker">
      <button
        data-testid="origin-picker-btn"
        onClick={() => setOpen((o) => !o)}
        title="Set where you're starting from"
        className={`flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold transition-colors max-w-[240px] ${
          value ? "bg-brand text-white" : "bg-white border border-border hover:border-brand"
        }`}
      >
        <MapPin size={15} strokeWidth={1.5} className="shrink-0" />
        <span className="truncate">Start: {label}</span>
        {value && (
          <X size={14} strokeWidth={2} className="shrink-0 hover:opacity-70"
            data-testid="origin-reset"
            onClick={(e) => { e.stopPropagation(); onChange(null); setPendingSave(null); }} />
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="absolute z-[1200] mt-2 w-80 max-w-[85vw] bg-white border border-border rounded-xl shadow-lg overflow-hidden"
            data-testid="origin-picker-panel"
          >
            <button
              data-testid="origin-use-current"
              onClick={() => { onChange(null); setPendingSave(null); setOpen(false); }}
              className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold hover:bg-secondary transition-colors border-b border-border"
            >
              <Crosshair size={15} strokeWidth={1.5} className="text-brand" /> Use my current location
            </button>

            {saved.length > 0 && (
              <div className="py-1 border-b border-border" data-testid="saved-origins">
                <div className="px-4 pt-1.5 pb-1 mono text-[11px] tracking-widest uppercase text-muted-foreground flex items-center gap-1">
                  <Star size={10} strokeWidth={1.5} /> Saved places
                </div>
                {saved.map((o) => (
                  <div key={o.id} data-testid={`saved-origin-${o.id}`}
                    className="group flex items-center gap-2 px-4 py-2 hover:bg-secondary transition-colors cursor-pointer"
                    onClick={() => pickSaved(o)}>
                    <MapPin size={14} strokeWidth={1.5} className="text-brand shrink-0" />
                    <span className="text-sm font-semibold flex-1 truncate">{o.label}</span>
                    <button data-testid={`saved-origin-delete-${o.id}`} onClick={(e) => removeSaved(e, o)}
                      title="Remove" className="opacity-0 group-hover:opacity-100 p-1 rounded hover:text-destructive transition-all">
                      <Trash2 size={13} strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="p-2">
              <div className="flex items-center gap-2 rounded-md border border-border px-2.5 py-2">
                <Search size={15} strokeWidth={1.5} className="text-muted-foreground shrink-0" />
                <input
                  data-testid="origin-search-input"
                  autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search an address or place…"
                  className="w-full text-sm bg-transparent outline-none"
                />
                {busy && <Loader2 size={14} className="animate-spin text-muted-foreground shrink-0" />}
              </div>
              {q.trim().length >= 3 && (
                <div className="mt-1.5 max-h-52 overflow-y-auto">
                  {results.length === 0 && !busy && (
                    <div className="px-2 py-3 text-xs text-muted-foreground">No matches — try a fuller address.</div>
                  )}
                  {results.map((res, i) => (
                    <button key={i} data-testid={`origin-suggestion-${i}`} onClick={() => pick(res)}
                      className="w-full text-left flex items-start gap-2 px-2 py-2 rounded-md hover:bg-secondary transition-colors">
                      <MapPin size={14} strokeWidth={1.5} className="text-brand mt-0.5 shrink-0" />
                      <span className="text-xs leading-snug">{res.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {pendingSave && (
              <div className="p-2 border-t border-border bg-secondary/40" data-testid="save-origin-box">
                <div className="mono text-[11px] tracking-widest uppercase text-muted-foreground px-1 pb-1 flex items-center gap-1">
                  <Plus size={10} strokeWidth={2} /> Save this as
                </div>
                <div className="flex items-center gap-1.5">
                  <input
                    data-testid="save-origin-name"
                    value={saveName} onChange={(e) => setSaveName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && confirmSave()}
                    placeholder='e.g. "Dorm"'
                    className="flex-1 text-sm rounded-md border border-border px-2.5 py-2 outline-none focus:ring-2 focus:ring-brand"
                  />
                  <button data-testid="save-origin-btn" onClick={confirmSave} disabled={!saveName.trim()}
                    className="bg-brand text-white rounded-md px-3 py-2 text-sm font-semibold hover:opacity-90 transition-colors disabled:opacity-40">
                    Save
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
